import { ILoadingAnimationSpinner } from 'library/ts/ui/components/loading/interfaces/ILoadingAnimationSpinner';
import { CSSValue } from 'library/ts/ui/enums/css/CSSValue';
import { Utilities } from 'library/ts/utilities/Utilities';

/**
 * @copyright © Paramount Streaming
 * @author Stan Gletski
 * @description Loading Animation Spinner component class TODO: make it inherit from AbstractUIComponent?
 */

export class LoadingAnimationSpinner implements ILoadingAnimationSpinner {

	#enabled: boolean;
	#svg: string;
	// element where Loading Animation should be attached to
	#container: HTMLElement;
	// CSS class responsible for SVG animation
	#cssAnimationClass: string;
	// CSS class responsible for SVG animation
	#jsRotationIntervalId: number | any;
	// reference to instance of Utilities
	#utils: Utilities;

	/**
	 * @param svg {String} svg string
	 * @param container {HTMLElement} partner container for loading animation
	 * @param cssAnimationClass {String} CSS class responsible for SVG animation
	 */
	constructor( svg: string, container: HTMLElement, cssAnimationClass: string = 'smart-tag-loading-animation' ) {
		this.#enabled = true;
		this.#utils = window.PARA.PlayerServices.Utilities;
		this.#svg = svg;
		this.#container = container;
		this.#cssAnimationClass = cssAnimationClass;
		this.#utils.CSS.injectCSS( this.#getSpinnerCSS() );
		this.#create();
	}

	get name(): string {
		return 'LoadingAnimationSpinner';
	}

	/**
	 * @description Returns whether the spinner is enabled
	 * @returns {boolean}
	 */
	get enabled(): boolean {
		return this.#enabled;
	}

	/**
	 * @description Enables the spinner
	 */
	enable = (): void => {
		this.#enabled = true;
	};

	/**
	 * @description Disables and hides the spinner
	 */
	disable = (): void => {
		this.#enabled = false;
		this.hide();
	};

	/**
	 * @description Starts JS-based svg rotation
	 */
	startRotationJS = (): void => {
		const svg: any = this.svgElement();
		if ( this.#utils.Type.isElement( svg ) === false ) {
			this.stopRotationJS();
			return;
		}
		this.#jsRotationIntervalId = setInterval(
			() => {
				if ( this.#utils.Type.isElement( svg ) ) {
					let rotation = this.#utils.DOM.getElementRotation( svg );
					rotation = rotation + 2;
					// Code for Safari
					svg.style.WebkitTransform = 'rotate(' + rotation + 'deg)';
					// Code for IE9
					svg.style.msTransform = 'rotate(' + rotation + 'deg)';
					// Standard syntax
					svg.style.transform = 'rotate(' + rotation + 'deg)';
				}
			},
			6
		);
	};

	/**
	 * @description Stops JS-based svg rotation
	 */
	stopRotationJS = (): void => {
		clearInterval( this.#jsRotationIntervalId );
		this.#jsRotationIntervalId = undefined;
	};

	/**
	 * @description Starts SVG based rotation
	 */
	startRotationCSS = () => {
		const svg: any = this.svgElement();
		if ( this.#utils.Type.isElement( svg ) === false ) return;
		if ( this.#utils.CSS.hasClass( svg, this.#cssAnimationClass ) === false ) {
			this.#utils.CSS.addClass( svg, this.#cssAnimationClass );
		}
	};

	/**
	 * @description Pauses SVG based rotation
	 */
	pauseRotationCSS = (): void => {
		const element: any = this.svgElement();
		if ( this.#utils.Type.isElement( element ) === false ) return;
		if ( this.#utils.CSS.hasClass( element, this.#cssAnimationClass ) ) this.#utils.CSS.removeClass( element, this.#cssAnimationClass );
	};

	/**
	 * @description Shows spinner
	 * @param {boolean} useInlineCSS
	 */
	show = ( useInlineCSS: boolean = true ): void => {
		if ( !this.#enabled ) {
			return;
		}

		if ( useInlineCSS === true ) {
			this.#container?.style?.setProperty(
				'opacity',
				'1',
				CSSValue.IMPORTANT
			);
		}

		const element = this.svgElement();

		// start CSS rotation
		if ( this.#utils.Type.isElement( element ) && !this.#utils.Type.isEmptyString( this.#cssAnimationClass ) && this.#utils.Type.isString( this.#cssAnimationClass ) ) {
			this.startRotationCSS();
		}
	};

	/**
	 * @description Hides spinner
	 * @param {boolean} useInlineCSS
	 */
	hide = ( useInlineCSS: boolean = true ): void => {
		if ( useInlineCSS === true ) {
			this.stop();

			this.#container?.style?.setProperty(
				'opacity',
				'0',
				CSSValue.IMPORTANT
			);
		}
	};

	/**
	 * @description Stop rotation
	 */
	stop = (): void => {
		this.stopRotationJS();
		this.pauseRotationCSS();
	};

	/**
	 * @description Returns SVG loading animation SVG element
	 * @return {Node}
	 */
	svgElement = () => {
		return this.#container?.getElementsByTagName( 'svg' )[ 0 ];
	};

	/**
	 * @description Create loading animation in container
	 */
	#create = (): void => {
		if ( this.#utils.Type.isUndefined( this.#container ) || this.#utils.Type.isUndefined( this.#svg ) ) return; // sanity check

		if ( this.#utils.Type.isElement( this.#container ) ) {
			this.#container.innerHTML = this.#svg;
			this.stopRotationJS();
			this.pauseRotationCSS();
		}
	};

	/**
	 * @description Returns a CSS injection string for SVG loading animation rotation / pause
	 * @return {String}
	 */
	#getSpinnerCSS = (): string => {
		const prefixes: string[] = [ '-webkit-', '-khtml-', '-moz-', '-ms-', '-o-', '' ]; // browser prefixes

		let classAnimation: string = '.' + this.#cssAnimationClass + ' {ANIMATION}';
		let classAnimationContent: string = '';
		let circleFillAnimation: string = '.' + this.#cssAnimationClass + ' .circle-fill {ANIMATION}';
		let circleFillAnimationContent: string = '';
		let keyFrames: string = '';

		for ( let i = 0; i < prefixes.length; i++ ) {
			classAnimationContent += ( prefixes[ i ] + 'animation: smart-tag-loader-spin 1.4s linear 0s infinite;' );
			circleFillAnimationContent += ( prefixes[ i ] + 'animation: smart-tag-loader-dash 1.4s ease-in-out 0s infinite;' );
			keyFrames += ( '\n' + '@' + prefixes[ i ] + 'keyframes smart-tag-loader-spin {' + '0% { transform: rotate(0deg); will-change: transform; } 100% { transform: rotate(360deg); will-change: transform; } }' );
			keyFrames += ( '\n' + '@' + prefixes[ i ] + 'keyframes smart-tag-loader-dash {' + '0% { stroke-dashoffset: 213.63; } 50% { stroke-dashoffset: 53.41; } 100% { stroke-dashoffset: 213.63; } }' );
		}

		classAnimation = ( classAnimation.replace( 'ANIMATION', classAnimationContent ) + '\n' );
		circleFillAnimation = ( circleFillAnimation.replace( 'ANIMATION', circleFillAnimationContent ) + '\n' );

		return ( classAnimation + circleFillAnimation + keyFrames );
	};
}